import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LayoutDashboard, Scissors, Bath, ShoppingBag, Star, Images, Smartphone, MessageSquare, FileText, Megaphone, Sparkles, Palette, Settings2, Activity, ShieldCheck } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { spring } from "../bits";
import { DevOverview, DevBookings, DevBoutique } from "./screens1";
import { DevReviews, DevGallery, DevAnnouncement, DevPopup, DevThemes } from "./screens2";
import { DevSms, DevTemplates, DevSettings, DevAppUsers, DevSystem } from "./screens3";

// In-app Management suite — every Dev capability, native premium mobile UI
const DevSuite = () => {
  const { t } = useLang();
  const m = t.mapp;
  const [screen, setScreen] = useState(null);

  const SECTIONS = [
    { id: "overview", label: m.sec_overview, icon: LayoutDashboard },
    { id: "grooming", label: m.sec_grooming, icon: Scissors },
    { id: "selfwash", label: m.sec_selfwash, icon: Bath },
    { id: "boutique", label: m.sec_boutique, icon: ShoppingBag },
    { id: "reviews", label: m.sec_reviews, icon: Star },
    { id: "gallery", label: m.sec_gallery, icon: Images },
    { id: "appusers", label: m.sec_appusers, icon: Smartphone },
    { id: "sms", label: m.sec_sms, icon: MessageSquare },
    { id: "templates", label: m.sec_templates, icon: FileText },
    { id: "announcement", label: m.sec_announcement, icon: Megaphone },
    { id: "popup", label: m.sec_popup, icon: Sparkles },
    { id: "themes", label: m.sec_themes, icon: Palette },
    { id: "settings", label: m.sec_settings, icon: Settings2 },
    { id: "system", label: m.sec_system, icon: Activity },
  ];

  const back = () => setScreen(null);

  return (
    <div data-testid="dev-suite">
      <AnimatePresence mode="wait">
        {screen === null ? (
          <motion.div key="hub" initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={spring}>
            <div className="flex items-center gap-3 mb-1.5">
              <div className="grid place-items-center h-11 w-11 rounded-xl bg-brand text-brand-cream shadow"><ShieldCheck className="h-5 w-5" /></div>
              <h1 className="font-heading text-3xl font-medium text-brand-ink">{m.tab_dev}</h1>
            </div>
            <p className="text-sm text-brand-muted mb-5">{m.dev_hub_sub}</p>
            <div className="grid grid-cols-2 gap-3 pb-28">
              {SECTIONS.map((s, i) => (
                <motion.button
                  key={s.id}
                  type="button"
                  initial={{ opacity: 0, y: 18, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ ...spring, delay: i * 0.045 }}
                  whileTap={{ scale: 0.93 }}
                  onClick={() => setScreen(s.id)}
                  className="rounded-2xl bg-white border border-brand/10 p-4 text-left shadow-[0_6px_24px_rgba(134,77,99,0.07)] hover:shadow-[0_12px_32px_rgba(134,77,99,0.14)] transition-shadow"
                  data-testid={`dev-hub-${s.id}`}
                >
                  <div className="grid place-items-center h-11 w-11 rounded-xl bg-brand-soft text-brand mb-2.5">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <p className="font-bold text-sm text-brand-ink leading-tight">{s.label}</p>
                </motion.button>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div key={screen} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 30 }} transition={spring}>
            {screen === "overview" && <DevOverview onBack={back} />}
            {screen === "grooming" && <DevBookings kind="grooming" onBack={back} />}
            {screen === "selfwash" && <DevBookings kind="selfwash" onBack={back} />}
            {screen === "boutique" && <DevBoutique onBack={back} />}
            {screen === "reviews" && <DevReviews onBack={back} />}
            {screen === "gallery" && <DevGallery onBack={back} />}
            {screen === "appusers" && <DevAppUsers onBack={back} />}
            {screen === "sms" && <DevSms onBack={back} />}
            {screen === "templates" && <DevTemplates onBack={back} />}
            {screen === "announcement" && <DevAnnouncement onBack={back} />}
            {screen === "popup" && <DevPopup onBack={back} />}
            {screen === "themes" && <DevThemes onBack={back} />}
            {screen === "settings" && <DevSettings onBack={back} />}
            {screen === "system" && <DevSystem onBack={back} />}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DevSuite;
